import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';

const CANVAS_W = 460;
const CANVAS_H = 170;

/**
 * Signature / initials capture: Draw (pointer canvas → trimmed PNG data-URI)
 * or Type (plain text; the backend renders it in the cursive font, the same
 * face previewed here via @font-face). OtterWaiver's SharedModal without the
 * react-signature-canvas / html-to-image dependencies.
 */
export default function SignatureModal({ open, onClose, kind = 'signature', defaultName, onComplete }) {
  const [tab, setTab] = useState('draw');
  const [typed, setTyped] = useState(defaultName ?? '');
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    if (!open) return;
    setTab('draw');
    setTyped(defaultName ?? '');
    setHasDrawn(false);
  }, [open, defaultName]);

  useEffect(() => {
    if (!open || tab !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = CANVAS_W * dpr;
    canvas.height = CANVAS_H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1e293b';
  }, [open, tab]);

  const pointerPos = (event) => {
    // Map from the canvas's rendered size to its logical drawing space —
    // `max-width: 100%` can shrink the element below CANVAS_W, and without
    // this scaling the stroke lands offset from the cursor.
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (CANVAS_W / rect.width),
      y: (event.clientY - rect.top) * (CANVAS_H / rect.height),
    };
  };

  const startStroke = (event) => {
    event.preventDefault();
    drawing.current = true;
    canvasRef.current.setPointerCapture(event.pointerId);
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pointerPos(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const moveStroke = (event) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pointerPos(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };
  const endStroke = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  /** Export the drawn strokes cropped to their bounding box (à la getTrimmedCanvas). */
  const exportDrawing = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const pixels = ctx.getImageData(0, 0, width, height).data;
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (pixels[(y * width + x) * 4 + 3] > 0) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < minX) return null;
    const pad = 4;
    const trimmed = document.createElement('canvas');
    trimmed.width = maxX - minX + pad * 2;
    trimmed.height = maxY - minY + pad * 2;
    trimmed
      .getContext('2d')
      .drawImage(canvas, minX - pad, minY - pad, trimmed.width, trimmed.height, 0, 0, trimmed.width, trimmed.height);
    return trimmed.toDataURL('image/png');
  };

  const save = () => {
    const value = tab === 'draw' ? exportDrawing() : typed.trim();
    if (!value) return;
    onComplete(value);
    onClose();
  };

  const title = kind === 'initial' ? 'Add your initials' : 'Add your signature';

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div className="flex gap-1 rounded-lg bg-slate-200/70 p-1">
          {['draw', 'type'].map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setTab(mode)}
              className={clsx(
                'flex-1 rounded-md py-1.5 text-sm font-medium capitalize transition-colors',
                tab === mode ? 'bg-slate-50 text-slate-900 shadow-sm' : 'text-slate-500',
              )}
            >
              {mode}
            </button>
          ))}
        </div>

        {tab === 'draw' ? (
          <div className="space-y-2">
            <canvas
              ref={canvasRef}
              style={{ width: CANVAS_W, height: CANVAS_H, maxWidth: '100%' }}
              className="touch-none rounded-lg border border-slate-300 bg-white"
              onPointerDown={startStroke}
              onPointerMove={moveStroke}
              onPointerUp={endStroke}
              onPointerCancel={endStroke}
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">Sign above with your mouse or finger</p>
              <button
                type="button"
                onClick={clear}
                className="text-xs font-medium text-slate-500 hover:text-slate-800"
              >
                Clear
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              maxLength={120}
              placeholder={kind === 'initial' ? 'Your initials' : 'Your full name'}
              className="block w-full rounded-lg border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm focus:border-brand-600 focus:outline-none"
            />
            <div className="flex h-[110px] items-center justify-center rounded-lg border border-[#e2e8f0] bg-white">
              <span
                className="max-w-full truncate px-4 text-4xl text-[#1e293b]"
                style={{ fontFamily: "'Dancing Script', cursive" }}
              >
                {typed || '…'}
              </span>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={tab === 'draw' ? !hasDrawn : !typed.trim()} onClick={save}>
            Apply
          </Button>
        </div>
      </div>
    </Modal>
  );
}
